// Updated QuotationHistory.jsx with Word Document Export

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  Visibility,
  FilterList,
  Download,
  PictureAsPdf,
  Description,
  MoreVert,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import { generateWordDocument } from './QuotationDoc'; // Import the Word export function

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const QuotationHistory = ({ onBack }) => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [downloadingDoc, setDownloadingDoc] = useState(false);

  // Fetch quotations
  useEffect(() => {
    fetchQuotations();
  }, [page, statusFilter]);

  const fetchQuotations = async () => {
    setLoading(true);
    setError(null);
    try {
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const response = await fetch(
        `${API_URL}/api/quotations?page=${page}&limit=20${statusParam}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setQuotations(data.data.quotations);
        setTotalPages(data.data.pagination.pages);
      } else {
        setError(data.message || 'Failed to fetch quotations');
      }
    } catch (err) {
      setError('Failed to fetch quotations: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle View Details
  const handleView = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/quotations/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setSelectedQuotation(data.data.quotation);
        setViewDialogOpen(true);
      }
    } catch (err) {
      setError('Failed to fetch quotation details');
    }
  };

  // Generate PDF (existing function)
  const generatePDF = async (quotation) => {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      pdf.setFont('helvetica');
      pdf.setFontSize(10);

      const formatNumber = (num) => {
        if (!num && num !== 0) return '0.00';
        const number = parseFloat(num);
        if (isNaN(number)) return '0.00';
        
        return new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number);
      };

      const getCurrencySymbol = () => {
        switch(quotation.currency) {
          case 'INR': return '₹';
          case 'USD': return '$';
          case 'EUR': return '€';
          case 'GBP': return '£';
          default: return '';
        }
      };

      const currencySymbol = getCurrencySymbol();
      const formatCurrency = (amount) => `${currencySymbol}${formatNumber(amount)}`;
      const addText = (text, x, y, options = {}) => {
        if (!text) text = '';
        const cleanText = String(text)
          .replace(/[^\x00-\x7F]/g, '')
          .replace(/[*]/g, '')
          .trim();
        pdf.text(cleanText, x, y, options);
      };

      let yPos = 20;

      // Company Header
      pdf.setFillColor(41, 128, 185);
      pdf.rect(10, 10, 190, 25, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);

      let leftX = 15;
      let leftY = 15;

      const leftInfo = [
        `Address: ${quotation.company_info?.address || ''}`,
        `Email: ${quotation.company_info?.email || ''}`,
        `Contact: ${quotation.company_info?.contact || ''}`,
        `GST/VAT: ${quotation.company_info?.gst_vat || ''}`
      ];

      leftInfo.forEach((line) => {
        if (line && line !== 'undefined') {
          addText(line, leftX, leftY);
          leftY += 5;
        }
      });

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      addText(quotation.company_info?.name || '', 190, 18, { align: 'right' });

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text('QUOTATION', 105, 42, { align: 'center' });
      pdf.setDrawColor(0, 0, 0);
      pdf.line(70, 44, 140, 44);

      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');

      let billStartY = 50;
      pdf.setFont('helvetica', 'bold');
      addText('Bill To:', 10, billStartY);
      pdf.setFont('helvetica', 'normal');
      let billY = billStartY + 5;

      const clientInfo = [
        quotation.client_info?.name || '',
        quotation.client_info?.address || '',
        `Contact: ${quotation.client_info?.contact || ''}`,
        `Email: ${quotation.client_info?.email || ''}`,
        `GST/VAT: ${quotation.client_info?.gst_vat || ''}`,
      ];

      clientInfo.forEach((line, index) => {
        if (line && line !== 'undefined') {
          addText(line, 15, billY + (index * 5));
        }
      });

      pdf.setFont('helvetica', 'bold');
      addText('Quotation Details', 110, billStartY);
      pdf.setFont('helvetica', 'normal');
      let detailsY = billStartY + 7;

      const formatDate = (dateStr) => {
        try {
          return new Date(dateStr).toLocaleDateString('en-IN');
        } catch {
          return dateStr || 'N/A';
        }
      };

      const quotationInfo = [
        [`Quotation #:`, quotation.quotation_number || 'Auto-generate'],
        [`Date:`, formatDate(quotation.date)],
        [`Valid Until:`, formatDate(quotation.valid_till)],
        [`Currency:`, quotation.currency || 'INR'],
      ];
      
      quotationInfo.forEach(([label, value], index) => {
        addText(label, 115, detailsY + (index * 5));
        addText(value, 150, detailsY + (index * 5));
      });

      const leftHeight = clientInfo.length * 3 + 15;
      const rightHeight = quotationInfo.length * 3 + 15;
      yPos = 45 + Math.max(leftHeight, rightHeight) + 8;

      if (quotation.scope_of_work) {
        pdf.setFont('helvetica', 'bold');
        addText('Scope of Work:', 10, yPos);
        pdf.setFont('helvetica', 'normal');
        
        const scopeLines = pdf.splitTextToSize(
          quotation.scope_of_work.replace(/[*\-•]/g, '').trim(), 
          180
        );
        
        scopeLines.forEach((line, index) => {
          addText(line, 15, yPos + 5 + (index * 5));
        });
        
        yPos += (scopeLines.length * 5) + 10;
      }

      yPos += 10;
      pdf.setFont('helvetica', 'bold');
      addText("Items & Services", 10, yPos);
      yPos += 6;

      const headers = ['#', 'Description', 'Unit', 'Unit Price', 'Qty', 'SubTotal', 'CGST%', 'SGST%', 'Amt(inc. GST)'];
      const colWidths = [10, 45, 15, 20, 15, 25, 15, 15, 30];
      let xPos = 10; 
      pdf.setFillColor(41, 128, 185);
      pdf.setTextColor(255, 255, 255);
      pdf.rect(xPos, yPos, 190, 8, 'F');

      let headerX = xPos;
      headers.forEach((h, i) => {
        pdf.text(h, headerX + colWidths[i] / 2, yPos + 6, { align: 'center' });
        headerX += colWidths[i];
      });

      pdf.setTextColor(0, 0, 0);
      yPos += 8;

      (quotation.items || []).forEach((item, index) => {
        let rowX = 10;
        const itemSubtotal = (item.qty || 0) * (item.unit_price || 0);
        const cgstAmount = itemSubtotal * ((item.cgst || 0) / 100);
        const sgstAmount = itemSubtotal * ((item.sgst || 0) / 100);
        const amountWithGST = itemSubtotal + cgstAmount + sgstAmount;

        let descriptionLines = pdf.splitTextToSize(
          String(item.description || "").replace(/[*\-•]/g, '').trim(),
          colWidths[1] - 4
        );

        let lineHeight = 6;
        let rowHeight = Math.max(descriptionLines.length * lineHeight, 8);
        let rowY = yPos;
        let cellX = 10;

        pdf.rect(cellX, rowY, colWidths[0], rowHeight);
        addText(String(item.item_no || index + 1), cellX + 2, rowY + 5);
        cellX += colWidths[0];

        pdf.rect(cellX, rowY, colWidths[1], rowHeight);
        descriptionLines.forEach((line, i) => {
          addText(line, cellX + 2, rowY + 5 + (i * lineHeight));
        });
        cellX += colWidths[1];

        pdf.rect(cellX, rowY, colWidths[2], rowHeight);
        addText(String(item.unit || ''), cellX + 2, rowY + 5);
        cellX += colWidths[2];

        pdf.rect(cellX, rowY, colWidths[3], rowHeight);
        addText(formatCurrency(item.unit_price || 0), cellX + 2, rowY + 5);
        cellX += colWidths[3];

        pdf.rect(cellX, rowY, colWidths[4], rowHeight);
        addText(String(item.qty || 0), cellX + 2, rowY + 5);
        cellX += colWidths[4];

        pdf.rect(cellX, rowY, colWidths[5], rowHeight);
        addText(formatCurrency(itemSubtotal), cellX + 2, rowY + 5);
        cellX += colWidths[5];

        pdf.rect(cellX, rowY, colWidths[6], rowHeight);
        addText(`${item.cgst || 0}%`, cellX + 2, rowY + 5);
        cellX += colWidths[6];

        pdf.rect(cellX, rowY, colWidths[7], rowHeight);
        addText(`${item.sgst || 0}%`, cellX + 2, rowY + 5);
        cellX += colWidths[7];

        pdf.rect(cellX, rowY, colWidths[8], rowHeight);
        addText(formatCurrency(amountWithGST), cellX + 2, rowY + 5);

        yPos += rowHeight;
      });

      yPos += 10;
      pdf.setFont('helvetica', 'bold');
      addText('Additional Costs:', 10, yPos);
      yPos += 5;

      pdf.setFont('helvetica', 'normal');

      const costLabelWidth = 120;
      const costValueWidth = 70;
      
      const additionalCosts = [
        ['Diagnostics/Evaluation Fee:', formatCurrency(parseFloat(quotation.diagnostics_fee || 0))],
        ['Labour & Repair Cost:', formatCurrency(parseFloat(quotation.labour_cost || 0))],
        ['Parts Estimate:', formatCurrency(parseFloat(quotation.parts_estimate || 0))],
        ['Testing & Commissioning:', formatCurrency(parseFloat(quotation.testing_and_commissioning || 0))],
        ['Packaging & Shipping:', formatCurrency(parseFloat(quotation.packaging_and_shipping || 0))],
      ];

      additionalCosts.forEach(([label, value]) => {
        pdf.rect(10, yPos, costLabelWidth, 8);
        pdf.rect(10 + costLabelWidth, yPos, costValueWidth, 8);
        addText(label, 12, yPos + 5);
        addText(value, 10 + costLabelWidth + costValueWidth - 5, yPos + 5, { align: 'right' });
        yPos += 8;
      });

      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const todayFormatted = `${day}_${month}_${year}`;

      const fileName = `Quotation_${quotation.quotation_number || 'Draft'}_${todayFormatted}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      //console.error('PDF generation error:', err);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  // Handle Download PDF
  const handleDownloadPDF = async (id, quotationNumber) => {
    try {
      let fullQuotation = selectedQuotation;
      if (!fullQuotation || fullQuotation.id !== id) {
        const response = await fetch(`${API_URL}/api/quotations/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch quotation details');

        const data = await response.json();
        if (data.success) {
          fullQuotation = data.data.quotation;
        } else {
          throw new Error('Failed to fetch quotation');
        }
      }

      await generatePDF(fullQuotation);
    } catch (err) {
      setError('Failed to download PDF: ' + err.message);
    }
  };

  // Handle Download Word Document
  const handleDownloadDoc = async (id) => {
    try {
      setDownloadingDoc(true);
      
      let fullQuotation = selectedQuotation;
      if (!fullQuotation || fullQuotation.id !== id) {
        const response = await fetch(`${API_URL}/api/quotations/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch quotation details');

        const data = await response.json();
        if (data.success) {
          fullQuotation = data.data.quotation;
        } else {
          throw new Error('Failed to fetch quotation');
        }
      }

      await generateWordDocument(fullQuotation);
    } catch (err) {
      setError('Failed to download Word document: ' + err.message);
    } finally {
      setDownloadingDoc(false);
      handleMenuClose();
    }
  };

  // Handle menu open/close
  const handleMenuOpen = (event, quotationId) => {
    setAnchorEl(event.currentTarget);
    setSelectedQuotationId(quotationId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedQuotationId(null);
  };

  // Format currency
  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      draft: 'warning',
      sent: 'info',
      accepted: 'success',
      rejected: 'error',
      expired: 'default',
    };
    return colors[status] || 'default';
  };

  // Filter quotations by search term
  const filteredQuotations = quotations.filter(q => 
    q.quotation_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.client_info.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && quotations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Quotation History
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search quotations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={fetchQuotations}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Quotations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Quotation #</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Valid Till</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQuotations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No quotations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredQuotations.map((quotation) => (
                <TableRow key={quotation.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {quotation.quotation_number}
                    </Typography>
                  </TableCell>
                  <TableCell>{quotation.client_info.name}</TableCell>
                  <TableCell>{new Date(quotation.date).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(quotation.valid_till).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(quotation.grand_total, quotation.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={quotation.status.toUpperCase()} 
                      color={getStatusColor(quotation.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(quotation.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleView(quotation.id)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={(e) => handleMenuOpen(e, quotation.id)}
                        title="Download Options"
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Download Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={() => {
            const quotation = quotations.find(q => q.id === selectedQuotationId);
            handleDownloadPDF(selectedQuotationId, quotation?.quotation_number);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <PictureAsPdf fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => handleDownloadDoc(selectedQuotationId)}
          disabled={downloadingDoc}
        >
          <ListItemIcon>
            <Description fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>
            {downloadingDoc ? 'Generating...' : 'Download Word (.docx)'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination 
          count={totalPages} 
          page={page} 
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Quotation Details</Typography>
            {selectedQuotation && (
              <Chip 
                label={selectedQuotation.status.toUpperCase()} 
                color={getStatusColor(selectedQuotation.status)}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedQuotation && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Quotation Number</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedQuotation.quotation_number}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body1">{new Date(selectedQuotation.date).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Valid Till</Typography>
                    <Typography variant="body1">{new Date(selectedQuotation.valid_till).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Currency</Typography>
                    <Typography variant="body1">{selectedQuotation.currency}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Financial Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      {formatCurrency(selectedQuotation.grand_total, selectedQuotation.currency)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedQuotation && (
            <>
              <Button 
                variant="outlined" 
                startIcon={<PictureAsPdf />}
                onClick={() => {
                  handleDownloadPDF(selectedQuotation.id, selectedQuotation.quotation_number);
                  setViewDialogOpen(false);
                }}
              >
                PDF
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Description />}
                onClick={() => {
                  handleDownloadDoc(selectedQuotation.id);
                  setViewDialogOpen(false);
                }}
                disabled={downloadingDoc}
              >
                {downloadingDoc ? 'Generating...' : 'Word'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotationHistory;